import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ArtworkObjectImage } from '../artwork/ArtworkObjectImage';

export interface MuseumHeroArtwork {
  artwork_id: number;
  student_name?: string;
  uploaded_at: string;
  updated_at?: string;
}

export type MuseumHeroStatus = 'loading' | 'ready' | 'empty' | 'error';

export interface MuseumHeroProps {
  artworks: MuseumHeroArtwork[];
  exhibitionTitle?: string;
  autoRotateInterval?: number;
  className?: string;
  status?: MuseumHeroStatus;
  errorMessage?: string;
  onRetry?: () => void;
}

const PAUSED_SESSION_KEY = 'rimu.home-gallery.explicit-pause';

const readExplicitPause = (): boolean => {
  try {
    return window.sessionStorage.getItem(PAUSED_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeExplicitPause = (paused: boolean): void => {
  try {
    window.sessionStorage.setItem(PAUSED_SESSION_KEY, String(paused));
  } catch {
    // Session storage can be unavailable in privacy-restricted browsers.
  }
};

const HeroSection = styled.section`
  width: 100%;
  background: ${({ theme }) => theme.colors.semantic.background};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  padding: clamp(1.5rem, 4vw, 3rem) 1rem clamp(1.25rem, 3vw, 2.5rem);
`;

const HeroInner = styled.div`
  width: min(1200px, 100%);
  margin: 0 auto;
`;

const CollectionLabel = styled.p`
  margin: 0 0 0.35rem;
  color: ${({ theme }) => theme.colors.semantic.textSecondary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  font-size: clamp(0.7rem, 1vw, 0.82rem);
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const CollectionTitle = styled.h1`
  margin: 0 0 clamp(0.75rem, 2vw, 1.25rem);
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
  font-size: clamp(1.8rem, 4vw, 3.75rem);
  font-weight: 500;
  line-height: 1.08;
`;

const MediaFrame = styled.div`
  position: relative;
  width: 100%;
  height: clamp(360px, 28vw, 390px);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.semantic.imageMat};
  border: 1px solid ${({ theme }) => theme.colors.semantic.border};
  touch-action: pan-y;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: clamp(220px, 66vw, 360px);
    aspect-ratio: 4 / 3;
  }
`;

const HeroImage = styled(ArtworkObjectImage)`
  display: block;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  object-fit: contain;
  object-position: center;
`;

const ImageState = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1rem;
  color: ${({ theme }) => theme.colors.semantic.textSecondary};
  text-align: center;
`;

const LoadingMessage = styled(ImageState)`
  background: ${({ theme }) => theme.colors.semantic.imageMat};
`;

const ErrorMessage = styled(ImageState)`
  background: ${({ theme }) => theme.colors.semantic.imageMat};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 60px;
  padding-top: 0.5rem;
`;

const ControlButton = styled.button`
  min-width: 44px;
  min-height: 44px;
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.semantic.surfaceRaised};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font: inherit;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.semantic.focus};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Counter = styled.span`
  min-width: 4.5rem;
  color: ${({ theme }) => theme.colors.semantic.textSecondary};
  font-size: 0.85rem;
  text-align: center;
`;

const StatusPanel = styled.div`
  display: grid;
  min-height: clamp(220px, 38vw, 360px);
  place-items: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  background: ${({ theme }) => theme.colors.semantic.imageMat};
  border: 1px solid ${({ theme }) => theme.colors.semantic.border};
  color: ${({ theme }) => theme.colors.semantic.textSecondary};
  text-align: center;
`;

const RetryButton = styled(ControlButton)`
  min-width: 0;
`;

const ImageRetryButton = styled(RetryButton)`
  margin-top: 0.25rem;
`;

export function MuseumHero({
  artworks,
  exhibitionTitle = 'Home Gallery',
  autoRotateInterval = 8000,
  className,
  status = 'ready',
  errorMessage = 'The Home Gallery is temporarily unavailable.',
  onRetry,
}: MuseumHeroProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExplicitlyPaused, setIsExplicitlyPaused] = useState(readExplicitPause);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [reducedMotionOverride, setReducedMotionOverride] = useState(false);
  const [isDocumentHidden, setIsDocumentHidden] = useState(
    () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
  );
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageAttempt, setImageAttempt] = useState(0);
  const [failedArtworkIds, setFailedArtworkIds] = useState<Set<number>>(() => new Set());
  const pointerStart = useRef<{ id: number; x: number; y: number } | null>(null);
  const selectedArtworkId = useRef<number | undefined>(undefined);
  const artworkSetRef = useRef(artworks);
  const heroRef = useRef<HTMLElement | null>(null);

  const currentArtwork = artworks[currentIndex];
  const artworkKey = useMemo(() => artworks.map(({ artwork_id, uploaded_at }) => `${artwork_id}:${uploaded_at}`).join(','), [artworks]);
  const artworkSetKeyRef = useRef(artworkKey);
  if (artworkSetKeyRef.current !== artworkKey) {
    artworkSetKeyRef.current = artworkKey;
    artworkSetRef.current = artworks;
  }

  useEffect(() => {
    const previousArtworkId = selectedArtworkId.current;
    const artworkSet = artworkSetRef.current;
    setCurrentIndex((previousIndex) => {
      if (artworkSet.length === 0) return 0;
      const preservedIndex = previousArtworkId === undefined
        ? -1
        : artworkSet.findIndex(({ artwork_id }) => artwork_id === previousArtworkId);
      return preservedIndex >= 0 ? preservedIndex : Math.min(previousIndex, artworkSet.length - 1);
    });
    setFailedArtworkIds(new Set());
    setImageAttempt(0);
    pointerStart.current = null;
    setIsDragging(false);
  }, [artworkKey]);

  useEffect(() => {
    selectedArtworkId.current = currentArtwork?.artwork_id;
    setImageLoaded(false);
    setImageError(false);
  }, [currentArtwork?.artwork_id, imageAttempt]);

  useEffect(() => {
    if (!isFocused || typeof document === 'undefined') return;
    const activeElement = document.activeElement;
    if (!activeElement || !heroRef.current?.contains(activeElement)) {
      setIsFocused(false);
    }
  }, [imageAttempt, imageError, imageLoaded, isFocused, status, currentArtwork?.artwork_id]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => {
      setIsReducedMotion(mediaQuery.matches);
      setReducedMotionOverride(false);
    };
    updatePreference();
    mediaQuery.addEventListener?.('change', updatePreference);
    return () => mediaQuery.removeEventListener?.('change', updatePreference);
  }, []);

  useEffect(() => {
    const updateVisibility = () => setIsDocumentHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', updateVisibility);
    return () => document.removeEventListener('visibilitychange', updateVisibility);
  }, []);

  const navigate = useCallback((direction: 'previous' | 'next') => {
    if (artworks.length < 2) return;
    setCurrentIndex((previousIndex) => direction === 'previous'
      ? (previousIndex === 0 ? artworks.length - 1 : previousIndex - 1)
      : (previousIndex + 1) % artworks.length);
  }, [artworks.length]);

  const togglePause = () => {
    if (isReducedMotion) {
      const currentlyPaused = isExplicitlyPaused || !reducedMotionOverride;
      const nextPaused = !currentlyPaused;
      setIsExplicitlyPaused(nextPaused);
      setReducedMotionOverride(!nextPaused);
      writeExplicitPause(nextPaused);
      return;
    }

    setIsExplicitlyPaused((previousPaused) => {
      const nextPaused = !previousPaused;
      writeExplicitPause(nextPaused);
      return nextPaused;
    });
  };

  const reducedMotionPaused = isReducedMotion && !reducedMotionOverride;
  const isPaused = isExplicitlyPaused || reducedMotionPaused;
  const shouldAutoRotate = status === 'ready' && artworks.length > 1 && !isPaused &&
    !isDocumentHidden && !isHovered && !isFocused && !isDragging;

  useEffect(() => {
    if (!shouldAutoRotate) return undefined;
    const interval = window.setInterval(() => {
      setCurrentIndex((previousIndex) => (previousIndex + 1) % artworks.length);
    }, autoRotateInterval);
    return () => window.clearInterval(interval);
  }, [artworks.length, autoRotateInterval, shouldAutoRotate]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as Element | null;
    if (target?.closest('button, a, input, select, textarea, [role="button"]')) return;
    if ((event.pointerType === 'mouse' && event.button !== 0) || pointerStart.current) return;
    pointerStart.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    if (!start || start.id !== event.pointerId) return;
    pointerStart.current = null;
    setIsDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (!start || artworks.length < 2) return;
    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    navigate(deltaX > 0 ? 'previous' : 'next');
  };

  const handleImageError = (artworkId: number, attempt: number) => {
    if (currentArtwork?.artwork_id !== artworkId || imageAttempt !== attempt) return;
    setFailedArtworkIds((previous) => new Set(previous).add(artworkId));
    setImageError(true);
    setImageLoaded(false);
  };

  const retryCurrentImage = () => {
    if (!currentArtwork) return;
    setFailedArtworkIds((previous) => {
      const next = new Set(previous);
      next.delete(currentArtwork.artwork_id);
      return next;
    });
    setImageError(false);
    setImageLoaded(false);
    setImageAttempt((attempt) => attempt + 1);
  };

  const handleImageLoad = (artworkId: number, attempt: number) => {
    if (currentArtwork?.artwork_id !== artworkId || imageAttempt !== attempt) return;
    setFailedArtworkIds((previous) => {
      if (!previous.has(artworkId)) return previous;
      const next = new Set(previous);
      next.delete(artworkId);
      return next;
    });
    setImageError(false);
    setImageLoaded(true);
  };

  const cancelPointer = (event?: React.PointerEvent<HTMLDivElement>) => {
    if (event && pointerStart.current?.id !== event.pointerId) return;
    if (event) event.currentTarget.releasePointerCapture?.(event.pointerId);
    pointerStart.current = null;
    setIsDragging(false);
  };

  const imageUnavailable = imageError || (currentArtwork ? failedArtworkIds.has(currentArtwork.artwork_id) : false);

  const renderCurrentImage = () => {
    if (!currentArtwork || imageUnavailable) {
      return imageUnavailable ? (
        <ErrorMessage role="status">
          <span>This image is temporarily unavailable.</span>
          <ImageRetryButton type="button" onClick={retryCurrentImage}>Try image again</ImageRetryButton>
        </ErrorMessage>
      ) : null;
    }

    return (
      <>
        <HeroImage
          key={`${currentArtwork.artwork_id}:${imageAttempt}`}
          artworkId={currentArtwork.artwork_id}
          variant="image"
          updatedAt={currentArtwork.updated_at}
          access="public"
          lazy={false}
          alt={currentArtwork.student_name && currentArtwork.student_name !== 'Unknown Student'
            ? `Artwork by ${currentArtwork.student_name}`
            : `${exhibitionTitle} image ${currentIndex + 1}`}
          loading={currentIndex === 0 ? 'eager' : 'lazy'}
          onLoad={() => handleImageLoad(currentArtwork.artwork_id, imageAttempt)}
          onError={() => handleImageError(currentArtwork.artwork_id, imageAttempt)}
          onLoadError={() => handleImageError(currentArtwork.artwork_id, imageAttempt)}
        />
        {!imageLoaded && <LoadingMessage>Loading image…</LoadingMessage>}
      </>
    );
  };

  const hasControls = artworks.length > 1;

  return (
    <HeroSection
      ref={heroRef}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsFocused(false);
      }}
      aria-label="Home Gallery"
    >
      <HeroInner>
        <CollectionLabel>Current collection</CollectionLabel>
        <CollectionTitle>{exhibitionTitle}</CollectionTitle>

        {status === 'loading' && <StatusPanel role="status">Loading the Home Gallery…</StatusPanel>}
        {status === 'error' && (
          <StatusPanel role="alert">
            <span>{errorMessage}</span>
            {onRetry && <RetryButton type="button" onClick={onRetry}>Try again</RetryButton>}
          </StatusPanel>
        )}
        {status === 'empty' && (
          <StatusPanel role="status">There are no images in the current collection.</StatusPanel>
        )}
        {status === 'ready' && artworks.length === 0 && (
          <StatusPanel role="status">There are no images in the current collection.</StatusPanel>
        )}
        {status === 'ready' && currentArtwork && (
          <>
            <MediaFrame
              data-testid="home-gallery-media"
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerEnd}
              onPointerCancel={cancelPointer}
            >
              {renderCurrentImage()}
            </MediaFrame>
            {hasControls && (
              <Controls aria-label="Home Gallery controls">
                <ControlButton
                  type="button"
                  onClick={() => navigate('previous')}
                  aria-label="Previous Home Gallery image"
                >
                  ‹
                </ControlButton>
                <Counter aria-label={`Image ${currentIndex + 1} of ${artworks.length}`}>
                  {currentIndex + 1} / {artworks.length}
                </Counter>
                <ControlButton
                  type="button"
                  onClick={togglePause}
                  aria-pressed={isPaused}
                  aria-label={isPaused ? 'Resume Home Gallery rotation' : 'Pause Home Gallery rotation'}
                >
                  {isPaused ? 'Play' : 'Pause'}
                </ControlButton>
                <ControlButton
                  type="button"
                  onClick={() => navigate('next')}
                  aria-label="Next Home Gallery image"
                >
                  ›
                </ControlButton>
              </Controls>
            )}
          </>
        )}
      </HeroInner>
    </HeroSection>
  );
}
